if (!customElements.get('product-premium-tabs')) {
  customElements.define(
    'product-premium-tabs',
    class ProductPremiumTabs extends HTMLElement {
      constructor() {
        super();
        this.activeIndex = -1;
        this.mqlDesktop = window.matchMedia('(min-width: 1100px)');
        this.handleMediaChange = this.handleMediaChange.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
      }

      connectedCallback() {
        this.refreshElements();
        if (!this.items.length) return;

        this.bindEvents();

        if (typeof this.mqlDesktop.addEventListener === 'function') {
          this.mqlDesktop.addEventListener('change', this.handleMediaChange);
        } else {
          this.mqlDesktop.addListener(this.handleMediaChange);
        }

        document.addEventListener('shopify:block:select', this.handleBlockSelect);

        const presetBlockId = this.dataset.activeBlockId || '';
        const presetIndex = this.findIndexByBlockId(presetBlockId);
        const firstOpenIndex = this.items.findIndex((item) => item.classList.contains('is-active'));
        const fallbackIndex = presetIndex >= 0 ? presetIndex : firstOpenIndex >= 0 ? firstOpenIndex : this.isDesktop ? 0 : -1;

        this.setActive(fallbackIndex, { force: true });
        this.handleMediaChange();
      }

      disconnectedCallback() {
        this.unbindEvents();

        if (typeof this.mqlDesktop.removeEventListener === 'function') {
          this.mqlDesktop.removeEventListener('change', this.handleMediaChange);
        } else {
          this.mqlDesktop.removeListener(this.handleMediaChange);
        }

        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      }

      refreshElements() {
        this.items = Array.from(this.querySelectorAll('[data-item]'));
        this.triggers = this.items.map((item) => item.querySelector('[data-trigger]'));
        this.panels = this.items.map((item) => item.querySelector('[data-panel]'));
      }

      bindEvents() {
        this.unbindEvents();

        this.boundTriggerHandlers = this.triggers.map((trigger, index) => {
          const click = () => this.handleTrigger(index);
          const keydown = (event) => this.handleKeydown(event, index);

          trigger?.addEventListener('click', click);
          trigger?.addEventListener('keydown', keydown);

          return { click, keydown };
        });
      }

      unbindEvents() {
        this.triggers?.forEach((trigger, index) => {
          const handlers = this.boundTriggerHandlers?.[index];
          if (trigger && handlers) {
            trigger.removeEventListener('click', handlers.click);
            trigger.removeEventListener('keydown', handlers.keydown);
          }
        });

        this.boundTriggerHandlers = [];
      }

      get isDesktop() {
        return this.mqlDesktop.matches;
      }

      get activeBlockId() {
        return this.items?.[this.activeIndex]?.dataset.blockId || '';
      }

      findIndexByBlockId(blockId) {
        return this.items.findIndex((item) => item.dataset.blockId === blockId);
      }

      setActiveByBlockId(blockId, { force = true, focus = false } = {}) {
        const index = this.findIndexByBlockId(blockId);

        if (index < 0) {
          if (this.activeIndex >= 0) return;

          this.setActive(this.isDesktop ? 0 : -1, { force: true, focus });
          return;
        }

        this.setActive(index, { force, focus });
      }

      handleTrigger(index) {
        if (!this.isDesktop && this.activeIndex === index) {
          this.setActive(-1, { force: true });
          return;
        }

        this.setActive(index, { force: true });
      }

      handleKeydown(event, index) {
        if (!this.isDesktop) return;

        const lastIndex = this.triggers.length - 1;
        let nextIndex = null;

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            nextIndex = index === lastIndex ? 0 : index + 1;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            nextIndex = index === 0 ? lastIndex : index - 1;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = lastIndex;
            break;
        }

        if (nextIndex === null) return;

        event.preventDefault();
        this.setActive(nextIndex, { force: true, focus: true });
      }

      handleMediaChange() {
        this.dataset.layout = this.isDesktop ? 'desktop' : 'mobile';

        if (this.isDesktop && this.activeIndex < 0) {
          this.setActive(0, { force: true });
          return;
        }

        this.syncState();
      }

      handleBlockSelect(event) {
        const blockElement = event.target?.closest?.('[data-block-id]');
        if (!blockElement || !this.contains(blockElement)) return;

        const index = this.findIndexByBlockId(blockElement.dataset.blockId);
        if (index < 0) return;

        this.setActive(index, { force: true, focus: true });
        this.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }

      setActive(index, { force = false, focus = false } = {}) {
        if (!force && index === this.activeIndex) return;
        if (index >= this.items.length) index = this.items.length - 1;
        if (index < -1) index = -1;

        this.activeIndex = index;
        this.dataset.activeBlockId = this.activeBlockId;
        this.syncState();

        if (focus && index >= 0) {
          this.triggers[index]?.focus();
        }
      }

      syncState() {
        const isDesktop = this.isDesktop;

        this.setAttribute('role', isDesktop ? 'tablist' : 'presentation');

        if (isDesktop) {
          this.setAttribute('aria-orientation', 'horizontal');
        } else {
          this.removeAttribute('aria-orientation');
        }

        this.items.forEach((item, index) => {
          const trigger = this.triggers[index];
          const panel = this.panels[index];
          const isActive = index === this.activeIndex;

          item.classList.toggle('is-active', isActive);
          if (isDesktop) {
            item.setAttribute('role', 'presentation');
          } else {
            item.removeAttribute('role');
          }

          if (trigger) {
            trigger.setAttribute('aria-expanded', String(isActive));

            if (isDesktop) {
              trigger.setAttribute('role', 'tab');
              trigger.setAttribute('aria-selected', String(isActive));
              trigger.tabIndex = isActive ? 0 : -1;
            } else {
              trigger.removeAttribute('role');
              trigger.removeAttribute('aria-selected');
              trigger.tabIndex = 0;
            }
          }

          if (panel) {
            panel.classList.toggle('is-active', isActive);
            panel.hidden = !isActive;

            if (isDesktop) {
              panel.setAttribute('role', 'tabpanel');
              if (trigger?.id) {
                panel.setAttribute('aria-labelledby', trigger.id);
              }
            } else {
              panel.removeAttribute('role');
              panel.removeAttribute('aria-labelledby');
            }
          }
        });
      }
    },
  );
}

if (!window.TodayMerchProductInfoExcerpt) {
  window.TodayMerchProductInfoExcerpt = true;
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const expansionFallbackDuration = 420;
  const countableWordPunctuation = /[,.…;:!?()[\]{}"“”«»/]/gu;
  const removableEmptyElements = 'p, li, ul, ol, h1, h2, h3, h4, h5, h6, blockquote';
  const meaningfulEmbeddedContent = 'img, svg, video, audio, iframe, table, hr';

  const isCountableWord = (value) => value.replace(countableWordPunctuation, '').trim() !== '';

  const trimLeadingWords = (fragment, wordLimit) => {
    let wordsToTrim = wordLimit;
    let foundContinuation = false;
    const walker = document.createTreeWalker(fragment, 4);
    const textNodes = [];

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    textNodes.forEach((textNode) => {
      if (wordsToTrim <= 0) return;

      const segments = (textNode.nodeValue || '').split(/(\s+)/u);
      const keptSegments = [];

      segments.forEach((segment) => {
        if (wordsToTrim > 0) {
          if (segment.trim() === '') return;
          if (isCountableWord(segment)) wordsToTrim -= 1;
          return;
        }

        keptSegments.push(segment);
      });

      let nextValue = keptSegments.join('');
      if (!foundContinuation) nextValue = nextValue.replace(/^\s+/u, '');
      textNode.nodeValue = nextValue;
      if (nextValue.trim() !== '') foundContinuation = true;
    });

    fragment.querySelectorAll(removableEmptyElements).forEach((element) => {
      if (element.textContent.trim() !== '' || element.querySelector(meaningfulEmbeddedContent)) return;
      element.remove();
    });

    return (
      fragment.textContent.trim() !== '' ||
      Boolean(fragment.querySelector(meaningfulEmbeddedContent))
    );
  };

  const prepareContinuation = (excerpt, inlineContinuation, blockContinuation) => {
    if (excerpt.dataset.productInfoExcerptContinuationReady === 'true') return true;

    const template = excerpt.querySelector('[data-product-info-excerpt-template]');
    if (!(template instanceof HTMLTemplateElement) || !inlineContinuation || !blockContinuation) return false;

    const wordLimit = Number.parseInt(excerpt.dataset.productInfoExcerptWordLimit || '23', 10);
    const fragment = template.content.cloneNode(true);
    const hasContinuation = trimLeadingWords(fragment, Number.isFinite(wordLimit) ? wordLimit : 23);
    if (!hasContinuation) return false;

    const firstBlock = fragment.firstElementChild;
    const canContinueInline = firstBlock?.matches('p');

    if (canContinueInline) {
      inlineContinuation.replaceChildren(...Array.from(firstBlock.childNodes));
      firstBlock.remove();
    }

    blockContinuation.replaceChildren(fragment);
    excerpt.dataset.productInfoExcerptHasInlineContinuation = String(
      inlineContinuation.textContent.trim() !== '' ||
      Boolean(inlineContinuation.querySelector(meaningfulEmbeddedContent)),
    );
    excerpt.dataset.productInfoExcerptHasBlockContinuation = String(
      blockContinuation.textContent.trim() !== '' ||
      Boolean(blockContinuation.querySelector(meaningfulEmbeddedContent)),
    );
    excerpt.dataset.productInfoExcerptContinuationReady = 'true';
    return true;
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-product-info-excerpt-toggle]');
    if (!trigger) return;

    const excerpt = trigger.closest('[data-product-info-excerpt]');
    const inlineContinuation = excerpt?.querySelector('[data-product-info-excerpt-inline-continuation]');
    const blockContinuation = excerpt?.querySelector('[data-product-info-excerpt-block-continuation]');
    if (!excerpt || !inlineContinuation || !blockContinuation) return;

    event.preventDefault();
    if (excerpt.dataset.productInfoExcerptState === 'expanding' || excerpt.classList.contains('is-expanded')) return;
    if (!prepareContinuation(excerpt, inlineContinuation, blockContinuation)) return;

    const activatedByKeyboard = event.detail === 0;
    const startHeight = excerpt.getBoundingClientRect().height;
    let expansionTimeout;
    let transitionEndHandler;
    let isFinished = false;

    const finishExpansion = () => {
      if (isFinished) return;
      isFinished = true;

      window.clearTimeout(expansionTimeout);
      if (transitionEndHandler) {
        excerpt.removeEventListener('transitionend', transitionEndHandler);
      }

      excerpt.classList.add('is-expanded');
      excerpt.classList.remove('is-expanding');
      excerpt.dataset.productInfoExcerptState = 'expanded';
      excerpt.removeAttribute('aria-busy');
      excerpt.style.height = 'auto';
      excerpt.style.overflow = 'visible';
      trigger.setAttribute('aria-hidden', 'true');
      trigger.tabIndex = -1;

      if (activatedByKeyboard) {
        const focusTarget = excerpt.dataset.productInfoExcerptHasInlineContinuation === 'true'
          ? inlineContinuation
          : blockContinuation;
        try {
          focusTarget.focus({ preventScroll: true });
        } catch (_error) {
          focusTarget.focus();
        }
      } else if (document.activeElement === trigger) {
        trigger.blur();
      }
    };

    excerpt.dataset.productInfoExcerptState = 'expanding';
    excerpt.setAttribute('aria-busy', 'true');
    excerpt.style.height = `${startHeight}px`;
    excerpt.style.overflow = 'hidden';
    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('aria-hidden', 'true');
    trigger.tabIndex = -1;

    if (excerpt.dataset.productInfoExcerptHasInlineContinuation === 'true') {
      inlineContinuation.hidden = false;
      inlineContinuation.setAttribute('aria-hidden', 'false');
    }

    if (excerpt.dataset.productInfoExcerptHasBlockContinuation === 'true') {
      blockContinuation.hidden = false;
      blockContinuation.setAttribute('aria-hidden', 'false');
    }

    if (reducedMotionQuery.matches) {
      finishExpansion();
      return;
    }

    excerpt.classList.add('is-expanding');
    const endHeight = excerpt.scrollHeight;
    transitionEndHandler = (transitionEvent) => {
      if (transitionEvent.target !== excerpt || transitionEvent.propertyName !== 'height') return;
      finishExpansion();
    };
    excerpt.addEventListener('transitionend', transitionEndHandler);

    void excerpt.offsetHeight;
    window.requestAnimationFrame(() => {
      if (excerpt.dataset.productInfoExcerptState !== 'expanding') return;
      excerpt.classList.add('is-expanded');
      excerpt.style.height = `${endHeight}px`;
    });

    expansionTimeout = window.setTimeout(finishExpansion, expansionFallbackDuration);
  });
}
